import { api } from '@/lib/apiClient';

export type LoyaltyClient = {
  id: number;
  dni: string;
  name: string;
  lastName: string;
  phone: string;
  email: string;
  active: boolean;
  cardNumber: string;
  balancePoints: number;
  balanceMoney: number;
};

export type LoyaltyStatus = {
  ok: boolean;
  health?: Record<string, unknown>;
  terminals?: Record<string, unknown> | Array<Record<string, unknown>>;
};

export type LoyaltyReport = {
  from: string | null;
  to: string | null;
  earnedMoney: number;
  redeemedMoney: number;
  totalDiscounted: number;
  redemptionCount: number;
  eligibleSalesAmount: number;
  transactions: Array<{
    id: string;
    ticket: string;
    type: 'earned' | 'redeemed';
    cardLastFour: string | null;
    operationAmount: number;
    eligibleAmount: number;
    earnedAmount: number;
    redeemedAmount: number;
    createdAt: string;
  }>;
};

export const LOYALTY_REWARD_RATE = 0.05;

export function estimateLoyaltyBalance(
  currentBalance: number,
  saleTotal: number,
  requestedRedemption = 0,
  eligibleAmount = saleTotal,
) {
  const redemption = Math.min(
    Math.max(0, requestedRedemption),
    Math.max(0, currentBalance),
    Math.max(0, saleTotal),
  );
  const paidAmount = Math.max(0, saleTotal - redemption);
  const paidRatio = saleTotal > 0 ? paidAmount / saleTotal : 0;
  const eligiblePaidAmount = Math.max(0, Math.min(eligibleAmount, saleTotal)) * paidRatio;
  const earnedMoney = eligiblePaidAmount * LOYALTY_REWARD_RATE;
  return {
    redemption,
    paidAmount,
    eligiblePaidAmount,
    earnedMoney,
    finalBalance: Math.max(0, currentBalance - redemption + earnedMoney),
  };
}

type LoyaltyClientPayload = {
  id?: number;
  dni?: string;
  nombre?: string;
  apellido?: string;
  celular?: string;
  email?: string;
  activo?: boolean;
  tarjeta?: { numero?: string; activa?: boolean };
  saldo_puntos?: number;
  dinero_disponible?: number;
  saldo_monto?: number;
};

function normalizeClient(payload: LoyaltyClientPayload): LoyaltyClient {
  return {
    id: Number(payload.id || 0),
    dni: String(payload.dni || ''),
    name: String(payload.nombre || ''),
    lastName: String(payload.apellido || ''),
    phone: String(payload.celular || ''),
    email: String(payload.email || ''),
    active: payload.activo !== false && payload.tarjeta?.activa !== false,
    cardNumber: String(payload.tarjeta?.numero || payload.dni || ''),
    balancePoints: Number(payload.saldo_puntos || 0),
    balanceMoney: Number(payload.dinero_disponible ?? payload.saldo_monto ?? 0),
  };
}

export async function findLoyaltyClient(dni: string): Promise<LoyaltyClient> {
  const response = await api.get<{ cliente: LoyaltyClientPayload }>(`/loyalty/clients/${dni.replace(/\D/g, '')}`);
  return normalizeClient(response.cliente || {});
}

export function getLoyaltyStatus() {
  return api.get<LoyaltyStatus>('/loyalty/status');
}

export function getLoyaltyReport(from?: string, to?: string) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  return api.get<LoyaltyReport>(`/loyalty/report?${params.toString()}`);
}

export async function createLoyaltyClient(input: {
  dni: string;
  nombre: string;
  apellido: string;
  telefono: string;
  email?: string;
}): Promise<LoyaltyClient> {
  const response = await api.post<{ cliente: LoyaltyClientPayload }>('/loyalty/clients', input);
  return normalizeClient(response.cliente || {});
}

export function earnLoyaltyPoints(input: {
  tarjeta: string;
  montoOperacion: number;
  ticket: string;
  confirmarMontoRiesgo?: boolean;
}) {
  return api.post<Record<string, unknown>>('/loyalty/sales/earn', input);
}

export function redeemLoyaltyPoints(input: {
  tarjeta: string;
  montoOperacion: number;
  montoDescuento: number;
  ticket: string;
  confirmarMontoRiesgo?: boolean;
}) {
  return api.post<Record<string, unknown>>('/loyalty/sales/redeem', input);
}
